import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useClientAuth } from '../../context/ClientAuthContext';
import clientApi from '../../services/clientApi';
import { isInChina, isInChinaSync } from '../../utils/regionDetect';
import { useLanguage } from '../../context/LanguageContext';
import { L, kicker as luxeKicker, display, btn, LuxeStyles, useIsDesktop } from '../../theme/luxe';

// Small/secondary text is never lighter than #6E685F (matches the approved artboard).
const MUTE = '#6E685F';
const kicker = (extra = {}) => luxeKicker({ color: MUTE, ...extra });

// Page-local refinements on top of the shared Luxe sheet.
const PAGE_CSS = `
.cl-social { transition: border-color .3s ${L.ease}, background .3s ${L.ease}; }
.cl-social:hover:not(:disabled) { border-color: ${L.ink} !important; background: ${L.card} !important; }
.cl-tab { position: relative; }
.cl-tab::after { content:''; position:absolute; left:0; right:0; bottom:-1px; height:1px; background:${L.ink};
  transform:scaleX(0); transform-origin:left; transition:transform .5s ${L.easeOut}; }
.cl-tab.cl-on::after { transform:scaleX(1); }
.cl-root .lx-input { border-bottom-color: rgba(17,17,17,.35); }
.cl-root .lx-input:focus { border-bottom-color: ${L.ink}; }
.cl-root .lx-input::placeholder { color: ${MUTE}; }
.cl-select { appearance:none; -webkit-appearance:none; cursor:pointer; }
.cl-eye:hover { color: ${L.ink} !important; }
.cl-otp:hover:not(:disabled) { background: ${L.ink} !important; color: ${L.paper} !important; }
.cl-btn-disabled.lx-btn:hover { background: ${L.ink} !important; color: ${L.paper} !important; }
@keyframes clMsg { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:none; } }
.cl-msg { animation: clMsg .45s ${L.easeOut} backwards; }
@media (prefers-reduced-motion: reduce) { .cl-msg { animation:none; } }
`;

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const WeChatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#07C160" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm3.68 4.025c-3.694 0-6.69 2.463-6.69 5.495s2.996 5.495 6.69 5.495a7.72 7.72 0 0 0 2.192-.32.657.657 0 0 1 .546.075l1.428.835a.254.254 0 0 0 .126.041.222.222 0 0 0 .218-.222c0-.055-.022-.109-.037-.163l-.292-1.11a.454.454 0 0 1 .16-.503C22.078 18.794 24 17.117 24 15.511c0-3.032-2.996-5.495-6.69-5.495h-.033zm-2.19 2.893c.483 0 .875.398.875.888a.882.882 0 0 1-.875.887.882.882 0 0 1-.875-.887c0-.49.392-.888.875-.888zm4.379 0c.483 0 .875.398.875.888a.882.882 0 0 1-.875.887.882.882 0 0 1-.874-.887c0-.49.391-.888.874-.888z"/>
  </svg>
);

export default function ClientLogin() {
  const { language, setLanguage } = useLanguage();
  const isZh = language === 'zh';
  // tr() — local bilingual helper, used to label the user-facing strings on
  // this page so the new top-right 中文/EN toggle actually changes something.
  const tr = (zh, en) => (isZh ? zh : en);
  const [loginMode, setLoginMode] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const isDesktop = useIsDesktop(900);
  const [socialSettings, setSocialSettings] = useState({ google: { enabled: false }, apple: { enabled: false }, wechat: { enabled: false } });

  const [socialLoading, setSocialLoading] = useState(null); // 'google' | 'apple' | null
  const [googleReady, setGoogleReady] = useState(false);
  const [appleReady, setAppleReady] = useState(false);
  const [chinaDetected, setChinaDetected] = useState(isInChinaSync());

  const navigate = useNavigate();
  const { clientLogin, clientOTPLogin, clientGoogleLogin, clientAppleLogin, isClientAuthenticated } = useClientAuth();

  useEffect(() => {
    if (isClientAuthenticated) navigate('/client');
  }, [isClientAuthenticated, navigate]);

  useEffect(() => {
    const fetchSocialSettings = async () => {
      try {
        const res = await clientApi.get('/client/auth/social-login-settings');
        if (res.data) setSocialSettings(res.data);
      } catch (e) { /* use defaults */ }
    };
    fetchSocialSettings();
  }, []);

  // Async China detection
  useEffect(() => {
    isInChina().then((result) => setChinaDetected(result));
  }, []);

  // Load Google Identity Services SDK when Google login is enabled (skip in China)
  useEffect(() => {
    if (!socialSettings.google?.enabled || !socialSettings.google?.client_id) return;
    if (chinaDetected) return;

    const setupGoogle = () => {
      if (window.google?.accounts?.oauth2) {
        setGoogleReady(true);
        return true;
      }
      return false;
    };

    const existingScript = document.getElementById('google-gsi-script');
    if (existingScript) {
      if (!setupGoogle()) {
        // Script tag exists but SDK not ready yet - wait for it
        const onLoad = () => setupGoogle();
        existingScript.addEventListener('load', onLoad);
        const interval = setInterval(() => {
          if (setupGoogle()) clearInterval(interval);
        }, 300);
        const timeout = setTimeout(() => clearInterval(interval), 10000);
        return () => {
          existingScript.removeEventListener('load', onLoad);
          clearInterval(interval);
          clearTimeout(timeout);
        };
      }
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setupGoogle();
    document.head.appendChild(script);
  }, [socialSettings.google?.enabled, socialSettings.google?.client_id, chinaDetected]);

  // Load Apple Sign-In JS SDK when Apple login is enabled
  useEffect(() => {
    if (!socialSettings.apple?.enabled || !socialSettings.apple?.client_id) return;

    const setupApple = () => {
      if (window.AppleID) {
        setAppleReady(true);
        return true;
      }
      return false;
    };

    const existingScript = document.getElementById('apple-signin-script');
    if (existingScript) {
      if (!setupApple()) {
        const onLoad = () => setupApple();
        existingScript.addEventListener('load', onLoad);
        const interval = setInterval(() => {
          if (setupApple()) clearInterval(interval);
        }, 300);
        const timeout = setTimeout(() => clearInterval(interval), 10000);
        return () => {
          existingScript.removeEventListener('load', onLoad);
          clearInterval(interval);
          clearTimeout(timeout);
        };
      }
      return;
    }
    const script = document.createElement('script');
    script.id = 'apple-signin-script';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;
    script.onload = () => setupApple();
    document.head.appendChild(script);
  }, [socialSettings.apple?.enabled, socialSettings.apple?.client_id]);

  // Google button click handler - uses OAuth2 token client for reliable popup flow
  const handleGoogleLogin = () => {
    if (!window.google?.accounts?.oauth2) {
      setError('Google login SDK not loaded, please refresh the page');
      return;
    }
    // Create OAuth2 token client and request access token via popup
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: socialSettings.google.client_id,
      scope: 'openid email profile',
      callback: async (tokenResponse) => {
        if (tokenResponse.error) return; // User cancelled or error
        setSocialLoading('google');
        setError('');
        try {
          const result = await clientGoogleLogin(tokenResponse.access_token);
          if (result.success) {
            setSuccess('登录成功，正在跳转...');
            setTimeout(() => navigate('/client'), 500);
          } else {
            setError(result.error || 'Google login failed');
          }
        } catch (err) {
          setError(err.message || 'Google login failed');
        } finally {
          setSocialLoading(null);
        }
      },
    });
    client.requestAccessToken();
  };

  // Apple button click handler
  const handleAppleLogin = async () => {
    if (!appleReady || !window.AppleID) {
      setError('Apple login is loading, please refresh the page');
      return;
    }
    setSocialLoading('apple');
    setError('');
    try {
      // Apple requires the redirectURI to EXACTLY match the Return URL registered
      // on the Service ID in Apple Developer Console. Admins configure that value
      // under /admin/settings → Social Login → Apple. Fall back to the current
      // origin + '/client/login' for installs that haven't set it yet.
      const configuredRedirect = (socialSettings.apple?.redirect_uri || '').trim();
      const redirectURI = configuredRedirect || (window.location.origin + '/client/login');
      window.AppleID.auth.init({
        clientId: socialSettings.apple.client_id,
        scope: 'name email',
        redirectURI,
        usePopup: true,
      });
      const response = await window.AppleID.auth.signIn();
      const idToken = response?.authorization?.id_token;
      const userData = response?.user || null;
      if (!idToken) {
        setError('Apple login failed: no token received');
        setSocialLoading(null);
        return;
      }
      const result = await clientAppleLogin(idToken, userData);
      if (result.success) {
        setSuccess('登录成功，正在跳转...');
        setTimeout(() => navigate('/client'), 500);
      } else {
        setError(result.error || 'Apple login failed');
      }
    } catch (err) {
      // User cancelled or error
      if (err.error !== 'popup_closed_by_user') {
        setError(err.message || err.error || 'Apple login failed');
      }
    } finally {
      setSocialLoading(null);
    }
  };

  useEffect(() => {
    if (otpCountdown <= 0) return;
    const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [otpCountdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (loginMode === 'password') {
      if (!email.trim() || !password.trim()) { setError('请填写所有必填字段'); return; }
      setLoading(true);
      try {
        const result = await clientLogin(email, password);
        if (result.success) { setSuccess('登录成功，正在跳转...'); setTimeout(() => navigate('/client'), 500); }
        else { setError(result.error || '登录失败，请检查您的凭据'); }
      } catch (err) { setError(err.message || '登录失败'); }
      finally { setLoading(false); }
    } else {
      if (!phone.trim() || !otpCode.trim()) { setError('请输入手机号和验证码'); return; }
      setLoading(true);
      try {
        const result = await clientOTPLogin(formatPhone(phone), otpCode);
        if (result.success) { setSuccess('登录成功，正在跳转...'); setTimeout(() => navigate('/client'), 500); }
        else { setError(result.error || '验证码无效'); }
      } catch (err) { setError(err.message || '验证码登录失败'); }
      finally { setLoading(false); }
    }
  };

  const handleSendOTP = async () => {
    if (!phone.trim()) { setError('请输入手机号'); return; }
    setError('');
    try {
      const res = await clientApi.post('/client/auth/send-otp', { phone: formatPhone(phone) });
      setOtpSent(true);
      setOtpCountdown(60);
      setSuccess(res.data?.debug_otp ? `验证码已发送 (测试: ${res.data.debug_otp})` : '验证码已发送');
    } catch (err) { setError(err.response?.data?.error || '发送验证码失败'); }
  };

  const countryCodes = [
    { code: '+1', flag: '🇺🇸', country: 'US' }, { code: '+86', flag: '🇨🇳', country: 'CN' },
    { code: '+852', flag: '🇭🇰', country: 'HK' }, { code: '+886', flag: '🇹🇼', country: 'TW' },
    { code: '+44', flag: '🇬🇧', country: 'UK' }, { code: '+65', flag: '🇸🇬', country: 'SG' },
    { code: '+81', flag: '🇯🇵', country: 'JP' }, { code: '+82', flag: '🇰🇷', country: 'KR' },
    { code: '+61', flag: '🇦🇺', country: 'AU' }, { code: '+64', flag: '🇳🇿', country: 'NZ' },
    { code: '+60', flag: '🇲🇾', country: 'MY' }, { code: '+66', flag: '🇹🇭', country: 'TH' },
    { code: '+63', flag: '🇵🇭', country: 'PH' }, { code: '+62', flag: '🇮🇩', country: 'ID' },
    { code: '+84', flag: '🇻🇳', country: 'VN' }, { code: '+91', flag: '🇮🇳', country: 'IN' },
    { code: '+49', flag: '🇩🇪', country: 'DE' }, { code: '+33', flag: '🇫🇷', country: 'FR' },
    { code: '+39', flag: '🇮🇹', country: 'IT' }, { code: '+34', flag: '🇪🇸', country: 'ES' },
    { code: '+7', flag: '🇷🇺', country: 'RU' }, { code: '+55', flag: '🇧🇷', country: 'BR' },
    { code: '+971', flag: '🇦🇪', country: 'AE' }, { code: '+966', flag: '🇸🇦', country: 'SA' },
  ];

  const formatPhone = (p) => `${countryCode}${p.replace(/[^\d]/g, '')}`;

  const hasSocialLogin = socialSettings.google?.enabled || socialSettings.apple?.enabled || socialSettings.wechat?.enabled;

  // ─── Presentation ──────────────────────────────────────────────────────────
  const labelStyle = kicker({ display: 'block', fontSize: 12, letterSpacing: '.22em', marginBottom: 2 });
  const field = { marginBottom: 22, position: 'relative' };
  const spinner = (dark) => ({
    width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
    border: `1.5px solid ${dark ? 'rgba(17,17,17,.18)' : 'rgba(246,243,238,.3)'}`,
    borderTopColor: dark ? L.ink : L.paper,
    animation: 'lxSpin .7s linear infinite',
  });
  const socialBtn = {
    width: '100%', height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
    background: 'transparent', border: `1px solid ${L.ink}`, borderRadius: L.radius, color: L.ink,
    fontFamily: L.sans, fontSize: 13.5, fontWeight: 400, letterSpacing: '.12em',
    cursor: 'pointer', marginBottom: 10,
  };
  const textLink = {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontFamily: L.sans, fontSize: 12.5, fontWeight: 500, letterSpacing: '.16em',
    textTransform: 'uppercase', color: L.ink,
  };

  const Wordmark = ({ size }) => (
    <div style={{
      fontFamily: L.serif, fontWeight: 500, fontSize: size, lineHeight: 1,
      letterSpacing: '.28em', marginRight: '-.28em', color: L.ink,
    }}>FINDU</div>
  );

  // Left half on desktop: an editorial "cover" composed in type.
  const coverPanel = (
    <aside style={{
      position: 'relative', flex: '1 1 50%', minHeight: '100vh', overflow: 'hidden',
      background: L.paper2, borderRight: `1px solid ${L.line}`,
    }}>
      {/* hairline frame */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 28, border: `1px solid ${L.line}`, pointerEvents: 'none',
      }} />

      <div style={{
        position: 'relative', zIndex: 2, height: '100%', minHeight: '100vh', boxSizing: 'border-box',
        padding: '64px 72px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
      }}>
        <div className="lx-fade lx-d1" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={kicker({ color: L.ink2, fontSize: 11.5, letterSpacing: '.26em' })}>{tr('第 07 期 · 会员专刊', 'Issue Nº 07 · Members')}</span>
          <span style={kicker({ fontSize: 11.5, letterSpacing: '.26em' })}>{tr('私享 · 仅限受邀', 'By invitation')}</span>
        </div>

        <div style={{ textAlign: 'center' }}>
          <div className="lx-fade-up lx-d2" style={kicker({ color: L.goldDeep, fontSize: 12, letterSpacing: '.32em', marginBottom: 26 })}>
            Maison of Love · 高端婚恋机构
          </div>
          <div className="lx-fade-up lx-d3"><Wordmark size="clamp(64px, 8.5vw, 132px)" /></div>
          <div className="lx-fade lx-d4" style={{ display: 'flex', justifyContent: 'center', margin: '34px 0 30px' }}>
            <div className="lx-rule lx-rule-draw" style={{ width: 120, background: L.goldSoft }} />
          </div>
          <p className="lx-fade-up lx-d5" style={display(30, {
            fontStyle: 'italic', fontWeight: 400, color: L.ink2, lineHeight: 1.3,
            maxWidth: 440, margin: '0 auto',
          })}>
            {tr('爱，是一生中最值得用心经营的事。', 'Love, curated with the patience of couture.')}
          </p>
        </div>

        <div className="lx-fade lx-d6" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <span style={kicker({ fontSize: 12, letterSpacing: '.26em' })}>{tr('私人会员入口', 'The Client Entrance')}</span>
          <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 18, color: MUTE }}>Est. FINDU</span>
        </div>
      </div>
    </aside>
  );

  const mobileMasthead = (
    <header style={{ textAlign: 'center', padding: '68px 16px 8px' }}>
      <div className="lx-fade-up lx-d1" style={kicker({ color: L.goldDeep, letterSpacing: '.28em', marginBottom: 16 })}>
        Maison of Love · 高端婚恋机构
      </div>
      <div className="lx-fade-up lx-d2"><Wordmark size={52} /></div>
      <div className="lx-fade lx-d3" style={{ display: 'flex', justifyContent: 'center', marginTop: 22 }}>
        <div className="lx-rule lx-rule-draw" style={{ width: 64, background: L.goldSoft }} />
      </div>
    </header>
  );

  const langToggle = (
    <div style={{
      position: 'fixed', top: 18, right: isDesktop ? 28 : 16, zIndex: 200,
      display: 'inline-flex', alignItems: 'center', gap: 14,
      fontFamily: L.sans, fontSize: 12.5, fontWeight: 500, letterSpacing: '.18em',
    }}>
      {[
        { code: 'zh', label: '中文' },
        { code: 'en', label: 'EN' },
      ].map((opt, i) => {
        const active = language === opt.code;
        return (
          <React.Fragment key={opt.code}>
            {i > 0 && <span aria-hidden="true" style={{ width: 1, height: 12, background: L.line }} />}
            <button
              type="button"
              onClick={() => setLanguage(opt.code)}
              className={`lx-link${active ? ' lx-active' : ''}`}
              aria-pressed={active}
              style={{
                background: 'none', borderTop: 0, borderLeft: 0, borderRight: 0, padding: '6px 0', cursor: 'pointer',
                borderBottom: `1px solid ${active ? L.ink : 'transparent'}`,
                color: active ? L.ink : MUTE, fontFamily: 'inherit', fontSize: 'inherit',
                fontWeight: 'inherit', letterSpacing: 'inherit',
              }}
            >
              {opt.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );

  const formPanel = (
    <main style={{
      flex: '1 1 50%', display: 'flex', alignItems: isDesktop ? 'center' : 'flex-start', justifyContent: 'center',
      padding: isDesktop ? '72px 48px' : '28px 16px 48px', boxSizing: 'border-box',
      minHeight: isDesktop ? '100vh' : 'auto',
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div className="lx-fade-up lx-d1" style={kicker({ color: L.goldDeep, fontSize: 12, letterSpacing: '.26em', marginBottom: 14 })}>
          {tr('会员登录', 'Member Sign In')}
        </div>
        <h1 className="lx-fade-up lx-d2" style={display(isDesktop ? 52 : 40, { marginBottom: 12 })}>
          {tr('欢迎回来', 'Welcome back')}
        </h1>
        <p className="lx-fade-up lx-d2" style={{
          fontFamily: L.sans, fontWeight: 300, fontSize: 14.5, lineHeight: 1.6, color: L.ink2, margin: '0 0 34px',
        }}>
          {tr('欢迎回来，请登录您的账号', 'Welcome back — sign in to your account')}
        </p>

        {/* Social login buttons */}
        {hasSocialLogin && (
          <div className="lx-fade-up lx-d3">
            {socialSettings.google?.enabled && !chinaDetected && (
              <button type="button" className="cl-social" style={socialBtn}
                disabled={socialLoading === 'google'}
                onClick={handleGoogleLogin}>
                {socialLoading === 'google' ? (
                  <><span style={spinner(true)} /><span>{tr('登录中...', 'Signing in…')}</span></>
                ) : (
                  <><GoogleIcon /> <span>使用Google登录</span></>
                )}
              </button>
            )}
            {socialSettings.google?.enabled && chinaDetected && (
              <div style={{ textAlign: 'center', fontSize: 13, color: MUTE, padding: '8px 0', marginBottom: 10, fontWeight: 300 }}>
                Google登录在您的地区不可用
              </div>
            )}
            {socialSettings.apple?.enabled && (
              <button type="button" className="cl-social" style={socialBtn}
                disabled={socialLoading === 'apple'}
                onClick={handleAppleLogin}>
                {socialLoading === 'apple' ? (
                  <><span style={spinner(true)} /><span>{tr('登录中...', 'Signing in…')}</span></>
                ) : (
                  <><AppleIcon /> <span>使用Apple登录</span></>
                )}
              </button>
            )}
            {socialSettings.wechat?.enabled && (
              <button type="button" className="cl-social" style={socialBtn}
                onClick={() => {}}>
                <WeChatIcon /> <span>微信登录</span>
              </button>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, margin: '22px 0 26px' }}>
              <div className="lx-rule" style={{ flex: 1 }} />
              <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 16, color: MUTE }}>{tr('或', 'or')}</span>
              <div className="lx-rule" style={{ flex: 1 }} />
            </div>
          </div>
        )}

        {/* Login mode tabs */}
        <div className="lx-fade-up lx-d3" role="tablist" style={{
          display: 'flex', gap: 28, borderBottom: `1px solid ${L.line}`, marginBottom: 28,
        }}>
          {[
            { mode: 'password', label: tr('密码登录', 'Password') },
            { mode: 'otp', label: tr('验证码登录', 'SMS Code') },
          ].map(t => {
            const on = loginMode === t.mode;
            return (
              <button key={t.mode} type="button" role="tab" aria-selected={on}
                className={`cl-tab${on ? ' cl-on' : ''}`}
                onClick={() => { setLoginMode(t.mode); setError(''); setSuccess(''); }}
                style={{
                  background: 'none', border: 'none', padding: '12px 0', cursor: 'pointer',
                  fontFamily: L.sans, fontSize: 13, fontWeight: 400, letterSpacing: '.18em',
                  color: on ? L.ink : MUTE, transition: `color .3s ${L.ease}`,
                }}>
                {t.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} autoComplete="off" className="lx-fade-up lx-d4">
          {error && (
            <div className="cl-msg" role="alert" style={{
              borderLeft: `2px solid ${L.danger}`, background: 'rgba(158,43,37,.05)', color: L.danger,
              padding: '11px 14px', fontSize: 13.5, lineHeight: 1.5, marginBottom: 22, fontWeight: 400,
            }}>{error}</div>
          )}
          {success && (
            <div className="cl-msg" role="status" style={{
              borderLeft: `2px solid ${L.gold}`, background: 'rgba(184,151,90,.08)', color: L.goldDeep,
              padding: '11px 14px', fontSize: 13.5, lineHeight: 1.5, marginBottom: 22, fontWeight: 400,
            }}>{success}</div>
          )}

          {loginMode === 'password' ? (
            <>
              <div style={field}>
                <label htmlFor="cl-ident" style={labelStyle}>{tr('账号', 'Account')}</label>
                <input id="cl-ident" type="text" className="lx-input"
                  placeholder={tr('手机号 / 邮箱 / 用户名', 'Phone / Email / Username')}
                  value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div style={field}>
                <label htmlFor="cl-pass" style={labelStyle}>{tr('密码', 'Password')}</label>
                <input id="cl-pass" type={showPassword ? 'text' : 'password'} className="lx-input"
                  placeholder={tr('密码', 'Password')}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  style={{ paddingRight: 36 }} />
                <button type="button" className="cl-eye"
                  aria-label={showPassword ? tr('隐藏密码', 'Hide password') : tr('显示密码', 'Show password')}
                  style={{
                    position: 'absolute', right: 0, bottom: 10, background: 'none', border: 'none',
                    cursor: 'pointer', color: MUTE, display: 'flex', alignItems: 'center', padding: 4,
                    transition: `color .3s ${L.ease}`,
                  }}
                  onClick={() => setShowPassword(v => !v)} tabIndex={-1}>
                  {showPassword ? <EyeOff size={17} strokeWidth={1.4} /> : <Eye size={17} strokeWidth={1.4} />}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={field}>
                <label htmlFor="cl-phone" style={labelStyle}>{tr('手机号', 'Phone number')}</label>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                  <select value={countryCode} onChange={(e) => setCountryCode(e.target.value)}
                    className="lx-input cl-select" aria-label={tr('国家/地区代码', 'Country code')}
                    style={{ width: 92, flexShrink: 0 }}>
                    {countryCodes.map(cc => (
                      <option key={cc.code} value={cc.code}>{cc.flag} {cc.code}</option>
                    ))}
                  </select>
                  <input id="cl-phone" type="tel" className="lx-input" placeholder={tr('手机号', 'Phone number')}
                    value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d\s\-()]/g, ''))}
                    style={{ flex: 1 }} />
                </div>
              </div>
              <div style={field}>
                <label htmlFor="cl-otp" style={labelStyle}>{tr('验证码', 'Verification code')}</label>
                <input id="cl-otp" type="text" className="lx-input" placeholder={tr('输入验证码', 'Enter SMS code')}
                  value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                  style={{ paddingRight: 120, letterSpacing: otpCode ? '.3em' : 'normal' }} maxLength={6} />
                <button type="button" className="cl-otp"
                  onClick={handleSendOTP} disabled={otpCountdown > 0}
                  style={{
                    position: 'absolute', right: 0, bottom: 8,
                    background: 'transparent', border: `1px solid ${otpCountdown > 0 ? L.line : L.ink}`,
                    color: otpCountdown > 0 ? MUTE : L.ink, borderRadius: L.radius,
                    cursor: otpCountdown > 0 ? 'not-allowed' : 'pointer',
                    fontFamily: L.sans, fontSize: 12, fontWeight: 500, letterSpacing: '.16em',
                    textTransform: 'uppercase', padding: '7px 12px', whiteSpace: 'nowrap',
                    transition: `all .3s ${L.ease}`,
                  }}>
                  {otpCountdown > 0 ? `${otpCountdown}s` : otpSent ? tr('重新发送', 'Resend') : tr('获取验证码', 'Send code')}
                </button>
              </div>
            </>
          )}

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap', margin: '4px 0 30px',
          }}>
            <label onClick={(e) => { e.preventDefault(); setAgreedTerms(v => !v); }} style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none',
              fontSize: 13, fontWeight: 300, color: L.ink2, lineHeight: 1.4,
            }}>
              <span aria-hidden="true" style={{
                width: 15, height: 15, flexShrink: 0, boxSizing: 'border-box',
                border: `1px solid ${agreedTerms ? L.ink : 'rgba(17,17,17,.35)'}`,
                background: agreedTerms ? L.ink : 'transparent', borderRadius: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', transition: `all .25s ${L.ease}`,
              }}>
                {agreedTerms && (
                  <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke={L.paper} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
              <span>
                {tr('已同意《', 'I agree to the ')}
                <a href="/terms" className="lx-link" onClick={(e) => e.stopPropagation()}
                  style={{ color: L.goldDeep, textDecoration: 'none', fontWeight: 400 }}>
                  {tr('服务条款免责协议', 'Terms of Service')}
                </a>
                {tr('》', '')}
              </span>
            </label>
            <button type="button" className="lx-link"
              style={{ ...textLink, fontSize: 13, fontWeight: 400, letterSpacing: '.12em', textTransform: 'none', color: L.goldDeep }}
              onClick={() => navigate('/forgot')}>
              {tr('忘记密码?', 'Forgot password?')}
            </button>
          </div>

          <button type="submit" disabled={loading}
            className={`lx-btn${loading ? ' cl-btn-disabled' : ''}`}
            style={{
              ...btn.primary, width: '100%', height: 46, padding: '0 26px', fontSize: 12.5, letterSpacing: '.3em',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.8 : 1,
            }}>
            {loading ? (
              <><span style={spinner(false)} /><span>{tr('登录中...', 'Signing in…')}</span></>
            ) : tr('提交', 'Submit')}
          </button>

          {/* Sign-up CTA — new users land here too; give them a clear path
              to the registration flow. */}
          <div className="lx-fade lx-d5" style={{
            marginTop: 36, paddingTop: 26, borderTop: `1px solid ${L.line}`,
            display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 12, flexWrap: 'wrap',
          }}>
            <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 18, color: L.ink2 }}>
              {tr('还没有账号？', "Don't have an account? ")}
            </span>
            <button type="button" style={{
              ...textLink, fontWeight: 400, letterSpacing: '.22em', textTransform: 'none', color: L.goldDeep,
              borderBottom: `1px solid ${L.goldDeep}`, paddingBottom: 2,
            }}
              onClick={() => navigate('/register')}>
              {tr('立即注册', 'Sign up')}
            </button>
          </div>
        </form>

        {!isDesktop && (
          <div className="lx-fade lx-d6" style={{
            marginTop: 44, textAlign: 'center', fontFamily: L.serif, fontStyle: 'italic', fontSize: 16, color: MUTE,
          }}>
            {tr('爱，是一生中最值得用心经营的事。', 'Love, curated with the patience of couture.')}
          </div>
        )}
      </div>
    </main>
  );

  return (
    <div className="lx-root cl-root" style={{
      minHeight: '100vh', display: 'flex', flexDirection: isDesktop ? 'row' : 'column',
    }}>
      <LuxeStyles />
      <style>{PAGE_CSS}</style>
      {langToggle}
      {isDesktop ? coverPanel : mobileMasthead}
      {formPanel}
    </div>
  );
}
